import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import httpClient from 'Service'
import { APP } from 'Constants/App'
import {
  IKoreanSchoolContent,
  KoreanSchoolContentBodyType,
  KoreanSchoolContentPayload,
} from 'Entities/koreanSchoolContent'
import { KoreanLanguagePath } from 'Constants/Navigation'
import { useKoreanLanguageConfig } from './useKoreanLanguageConfig'

export type KoreanSchoolContentFormValues = Omit<
  IKoreanSchoolContent,
  'id' | 'type' | 'createdDate' | 'lastModifiedDate'
>

const emptyFormValues = (): KoreanSchoolContentFormValues => ({
  titleRu: '',
  titleUz: '',
  titleKr: '',
  contentRu: '',
  contentUz: '',
  contentKr: '',
})

/** Shared with the form UI for live “all languages complete” checks */
export const koreanSchoolContentFormSchema = yup.object({
  titleKr: yup.string().required(APP.REQUIRED_FIELD),
  titleRu: yup.string().required(APP.REQUIRED_FIELD),
  titleUz: yup.string().required(APP.REQUIRED_FIELD),
  contentKr: yup.string().required(APP.REQUIRED_FIELD),
  contentRu: yup.string().required(APP.REQUIRED_FIELD),
  contentUz: yup.string().required(APP.REQUIRED_FIELD),
})

export function areKoreanSchoolContentValuesEqual(
  a: KoreanSchoolContentFormValues,
  b: KoreanSchoolContentFormValues,
): boolean {
  const keys: (keyof KoreanSchoolContentFormValues)[] = [
    'titleRu',
    'titleUz',
    'titleKr',
    'contentRu',
    'contentUz',
    'contentKr',
  ]
  return keys.every(k => String(a[k] ?? '').trim() === String(b[k] ?? '').trim())
}

const invalidateSchoolContentQueries = (queryClient: ReturnType<typeof useQueryClient>, contentId?: string) => {
  queryClient.invalidateQueries({ queryKey: ['koreanSchoolContents'] })
  if (contentId) {
    queryClient.invalidateQueries({ queryKey: [`koreanSchoolContent-one-${contentId}`] })
  }
}

export const useKoreanSchoolContentForm = (
  bodyType: KoreanSchoolContentBodyType,
  id?: string,
  seed?: KoreanSchoolContentFormValues | null,
) => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { subPath } = useKoreanLanguageConfig()

  const defaultValues = useMemo(
    () => ({
      ...emptyFormValues(),
      ...(seed ?? {}),
    }),
    [seed],
  )

  const form = useForm<KoreanSchoolContentFormValues>({
    defaultValues,
    resolver: yupResolver(koreanSchoolContentFormSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const create = useMutation({
    mutationFn: (payload: KoreanSchoolContentPayload) =>
      httpClient.post<IKoreanSchoolContent>('/api/account/korean-school-contents', payload),
    onSuccess: () => {
      toast.success(APP.DATA_SUCCESS_SAVED)
      invalidateSchoolContentQueries(queryClient)
      navigate(`/${KoreanLanguagePath.main}/${subPath}`)
    },
    onError: () => {
      toast.error('Не удалось сохранить данные.')
    },
  })

  const update = useMutation({
    mutationFn: ({ contentId, payload }: { contentId: string; payload: KoreanSchoolContentPayload }) =>
      httpClient.patch<IKoreanSchoolContent>(`/api/account/korean-school-contents/${contentId}`, payload),
    onSuccess: () => {
      toast.success(APP.DATA_SUCCESS_CHANGED)
      invalidateSchoolContentQueries(queryClient, id)
      navigate(`/${KoreanLanguagePath.main}/${subPath}`)
    },
    onError: () => {
      toast.error('Не удалось сохранить данные.')
    },
  })

  const onSubmit = (values: KoreanSchoolContentFormValues) => {
    const payload: KoreanSchoolContentPayload = {
      type: bodyType,
      ...values,
    }
    if (id) {
      update.mutate({ contentId: id, payload })
    } else {
      create.mutate(payload)
    }
  }

  return {
    form,
    onSubmit,
    isLoading: create.isLoading || update.isLoading,
  }
}
